import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Form, Modal, Row } from "react-bootstrap";
import DataTable from "react-data-table-component";
import moment from "moment";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import DataTableSkeleton from "../../components/DataTableSkeleton";
import AdminEmptyState from "../../components/admin/AdminEmptyState";
import {
  approveAdminCommission,
  bulkApproveAdminCommissions,
  cancelAdminCommission,
  COMMISSION_STATUSES,
  formatCurrency,
  getAdminCommissionDetails,
  getAdminCommissions,
  getCommissionStatusBadge,
  normalizeCommissionList,
  normalizePagination,
} from "../../helper/affiliate_helper";
import { dataTableCustomStyles, dataTablePaginationOptions } from "../../components/admin/dataTableConfig";
import AdminRowActions, { AdminActionButton } from '../../components/admin/AdminRowActions';

const getCommissionId = (row) => row?.id || row?._id || null;

const getAffiliateName = (row) =>
  row?.affiliate?.name || row?.affiliateName || "—";

const getAffiliateEmail = (row) =>
  row?.affiliate?.email || row?.affiliateEmail || "—";

const getReferralCode = (row) =>
  row?.affiliate?.referralCode ||
  row?.affiliateReferralCode ||
  row?.referralCode ||
  "—";

const getAffiliateId = (row) =>
  row?.affiliate?.id || row?.affiliateId || null;

function CommissionDetailContent({ commission, loading }) {
  if (loading) {
    return <p className="text-muted mb-0">Loading details…</p>;
  }
  if (!commission) {
    return <p className="text-muted mb-0">No details available.</p>;
  }

  return (
    <div className="table-responsive">
      <table className="table table-bordered mb-0">
        <tbody>
          <tr><th style={{ width: "200px" }}>Commission ID</th><td><code className="small">{getCommissionId(commission)}</code></td></tr>
          <tr><th>Affiliate</th><td>{getAffiliateName(commission)}<div className="text-muted small">{getAffiliateEmail(commission)}</div></td></tr>
          <tr><th>Referral code</th><td>{getReferralCode(commission)}</td></tr>
          <tr><th>Order number</th><td>{commission.orderNumber || "—"}</td></tr>
          <tr><th>Order status</th><td>{commission.orderStatus || "—"}</td></tr>
          <tr><th>Tracking code</th><td>{commission.trackingCode || "—"}</td></tr>
          <tr><th>Amount</th><td>{formatCurrency(commission.commissionAmount, commission.currency)}</td></tr>
          <tr><th>Rate</th><td>{commission.commissionRate != null ? `${commission.commissionRate}%` : "—"}</td></tr>
          <tr><th>Status</th><td><Badge bg={getCommissionStatusBadge(commission.status)}>{commission.status}</Badge></td></tr>
          <tr><th>Created</th><td>{commission.createdAt ? moment(commission.createdAt).format("DD MMM YYYY, hh:mm A") : "—"}</td></tr>
          {commission.cancellationReason && (
            <tr><th>Cancel reason</th><td>{commission.cancellationReason}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function AffiliateCommissions() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [status, setStatus] = useState(searchParams.get("status") ?? "");
  const [affiliateId, setAffiliateId] = useState(searchParams.get("affiliateId") || "");
  const [fromDate, setFromDate] = useState(searchParams.get("from") || "");
  const [toDate, setToDate] = useState(searchParams.get("to") || "");

  const [selectedRows, setSelectedRows] = useState([]);
  const [detailCommission, setDetailCommission] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  const fetchCommissions = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (status) params.status = status;
      if (affiliateId.trim()) params.affiliateId = affiliateId.trim();
      if (fromDate) params.from = fromDate;
      if (toDate) params.to = toDate;

      const result = await getAdminCommissions(params);
      const list = normalizeCommissionList(result);
      const pagination = normalizePagination(result);

      if (result?.success === false && !list.length) {
        toast.error(result?.message || "Failed to load commissions");
        setData([]);
        setTotalPages(1);
        setTotalRecords(0);
        return;
      }

      setData(list);
      if (pagination) {
        setTotalPages(Math.max(pagination.pages, 1));
        setTotalRecords(pagination.total);
      } else {
        setTotalPages(Math.max(Math.ceil(list.length / limit), 1));
        setTotalRecords(list.length);
      }
    } catch (error) {
      if (error?.status !== 401) {
        console.error("Failed to fetch commissions:", error);
        toast.error(error?.message || "Failed to load commissions");
      }
      setData([]);
      setTotalPages(1);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommissions();
  }, [page, limit, status, affiliateId, fromDate, toDate]);

  useEffect(() => {
    const next = {};
    if (status) next.status = status;
    if (affiliateId.trim()) next.affiliateId = affiliateId.trim();
    if (fromDate) next.from = fromDate;
    if (toDate) next.to = toDate;
    setSearchParams(next, { replace: true });
  }, [status, affiliateId, fromDate, toDate, setSearchParams]);

  const openDetail = async (row) => {
    const commissionId = getCommissionId(row);
    if (!commissionId) return;

    setShowDetail(true);
    setDetailCommission(row);
    setDetailLoading(true);
    try {
      const res = await getAdminCommissionDetails(commissionId);
      if (res?.success === false) {
        toast.error(res?.message || "Failed to load commission details");
        return;
      }
      setDetailCommission(res?.data?.commission || res?.data || row);
    } catch (error) {
      if (error?.status !== 401) {
        toast.error(error?.message || "Failed to load commission details");
      }
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApprove = async (row) => {
    const commissionId = getCommissionId(row);
    if (!commissionId) return;

    setActionLoadingId(commissionId);
    try {
      const res = await approveAdminCommission(commissionId);
      if (res?.success === false) {
        toast.error(res?.message || "Failed to approve commission");
        return;
      }
      toast.success(res?.message || "Commission approved");
      fetchCommissions();
    } catch (error) {
      if (error?.status !== 401) {
        toast.error(error?.message || "Failed to approve commission");
      }
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleBulkApprove = async () => {
    const ids = selectedRows.map(getCommissionId).filter(Boolean);
    if (!ids.length) {
      toast.warning("Select pending commissions to approve");
      return;
    }

    setBulkLoading(true);
    try {
      const res = await bulkApproveAdminCommissions(ids);
      if (res?.success === false) {
        toast.error(res?.message || "Bulk approve failed");
        return;
      }
      toast.success(res?.message || "Commissions approved");
      setSelectedRows([]);
      fetchCommissions();
    } catch (error) {
      if (error?.status !== 401) {
        toast.error(error?.message || "Bulk approve failed");
      }
    } finally {
      setBulkLoading(false);
    }
  };

  const handleReject = async () => {
    const commissionId = getCommissionId(rejectTarget);
    const reason = rejectReason.trim();
    if (!commissionId || !reason) {
      toast.warning("Cancellation reason is required");
      return;
    }

    setActionLoadingId(commissionId);
    try {
      const res = await cancelAdminCommission(commissionId, reason);
      if (res?.success === false) {
        toast.error(res?.message || "Failed to cancel commission");
        return;
      }
      toast.success(res?.message || "Commission cancelled");
      setRejectTarget(null);
      setRejectReason("");
      fetchCommissions();
    } catch (error) {
      if (error?.status !== 401) {
        toast.error(error?.message || "Failed to cancel commission");
      }
    } finally {
      setActionLoadingId(null);
    }
  };

  const handlePaySelected = () => {
    const approvedRows = selectedRows.filter((row) => row.status === "approved");
    if (!approvedRows.length) {
      toast.warning("Select approved commissions from a single affiliate to pay");
      return;
    }

    const affiliateIds = [...new Set(approvedRows.map(getAffiliateId).filter(Boolean))];
    if (affiliateIds.length !== 1) {
      toast.warning("Pay selected works for one affiliate at a time");
      return;
    }

    const commissionIds = approvedRows.map(getCommissionId).filter(Boolean);
    const params = new URLSearchParams({
      affiliateId: affiliateIds[0],
      commissionIds: commissionIds.join(","),
    });
    navigate(`/affiliate-payouts/new?${params.toString()}`);
  };

  const selectableRowDisabled = (row) => row.status !== "pending" && row.status !== "approved";

  const columns = useMemo(() => [
    {
      name: "Affiliate",
      minWidth: "180px",
      cell: (row) => (
        <div>
          <div className="fw-medium">{getAffiliateName(row)}</div>
          <div className="text-muted small">{getAffiliateEmail(row)}</div>
        </div>
      ),
    },
    {
      name: "Referral",
      selector: (row) => getReferralCode(row),
      minWidth: "110px",
    },
    {
      name: "Order",
      selector: (row) => row.orderNumber || "—",
      minWidth: "140px",
    },
    {
      name: "Amount",
      selector: (row) => row.commissionAmount ?? 0,
      sortable: true,
      minWidth: "110px",
      cell: (row) => formatCurrency(row.commissionAmount, row.currency),
    },
    {
      name: "Rate",
      selector: (row) => row.commissionRate ?? 0,
      minWidth: "80px",
      cell: (row) => (row.commissionRate != null ? `${row.commissionRate}%` : "—"),
    },
    {
      name: "Status",
      selector: (row) => row.status,
      minWidth: "110px",
      cell: (row) => (
        <Badge bg={getCommissionStatusBadge(row.status)}>{row.status}</Badge>
      ),
    },
    {
      name: "Date",
      selector: (row) => (row.createdAt ? moment(row.createdAt).valueOf() : 0),
      sortable: true,
      minWidth: "130px",
      cell: (row) =>
        row.createdAt ? moment(row.createdAt).format("DD MMM YYYY") : "—",
    },
    {
      name: "Actions",
      minWidth: "200px",
      ignoreRowClick: true,
      cell: (row) => {
        const commissionId = getCommissionId(row);
        const isLoading = actionLoadingId === commissionId;

        return (
          <AdminRowActions>
            <AdminActionButton action="view" label="View details" onClick={() => openDetail(row)} />
            {row.status === "pending" && (
              <AdminActionButton
                icon="ri-check-line"
                label="Approve"
                tone="success"
                disabled={isLoading}
                onClick={() => handleApprove(row)}
              >
                Approve
              </AdminActionButton>
            )}
            {(row.status === "pending" || row.status === "approved") && (
              <AdminActionButton
                icon="ri-close-line"
                label="Reject"
                tone="danger"
                disabled={isLoading}
                onClick={() => {
                  setRejectTarget(row);
                  setRejectReason("");
                }}
              >
                Reject
              </AdminActionButton>
            )}
          </AdminRowActions>
        );
      },
    },
  ], [actionLoadingId]);

  const pendingSelectedCount = selectedRows.filter((row) => row.status === "pending").length;
  const approvedSelectedCount = selectedRows.filter((row) => row.status === "approved").length;

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="/admin/dashboard">Dashboard</Link></li>
              <li className="breadcrumb-item active" aria-current="page">Affiliate Commissions</li>
            </ol>
            <h4 className="main-title mb-0 admin-page-title">Affiliate Commissions</h4>
            <p className="text-secondary small mb-0 mt-1">
              Review, approve, and prepare commissions for manual payout
            </p>
          </div>
          <Button variant="primary" onClick={fetchCommissions} disabled={loading} className="mt-3 mt-md-0">
            <i className={`ri-refresh-line me-1 ${loading ? "spin" : ""}`} />
            Refresh
          </Button>
        </div>

        <Card className="border-0 shadow-sm mb-3">
          <Card.Body>
            <Row className="g-2 align-items-end">
              <Col md={3} sm={6}>
                <Form.Label className="small text-muted mb-1">Status</Form.Label>
                <Form.Select
                  value={status}
                  onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                >
                  <option value="">All statuses</option>
                  {COMMISSION_STATUSES.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3} sm={6}>
                <Form.Label className="small text-muted mb-1">Affiliate ID</Form.Label>
                <Form.Control
                  value={affiliateId}
                  onChange={(e) => { setAffiliateId(e.target.value); setPage(1); }}
                  placeholder="Filter by affiliate ID"
                />
              </Col>
              <Col md={2} sm={6}>
                <Form.Label className="small text-muted mb-1">From</Form.Label>
                <Form.Control
                  type="date"
                  value={fromDate}
                  onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
                />
              </Col>
              <Col md={2} sm={6}>
                <Form.Label className="small text-muted mb-1">To</Form.Label>
                <Form.Control
                  type="date"
                  value={toDate}
                  onChange={(e) => { setToDate(e.target.value); setPage(1); }}
                />
              </Col>
              <Col md={2} sm={6}>
                <Button
                  variant="outline-secondary"
                  className="w-100"
                  onClick={() => {
                    setStatus("");
                    setAffiliateId("");
                    setFromDate("");
                    setToDate("");
                    setPage(1);
                  }}
                >
                  Reset
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="border-0 shadow-sm">
          <Card.Body>
            {selectedRows.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mb-3">
                {pendingSelectedCount > 0 && (
                  <Button variant="success" size="sm" disabled={bulkLoading} onClick={handleBulkApprove}>
                    Approve selected ({pendingSelectedCount})
                  </Button>
                )}
                {approvedSelectedCount > 0 && (
                  <Button variant="primary" size="sm" onClick={handlePaySelected}>
                    Pay selected ({approvedSelectedCount})
                  </Button>
                )}
              </div>
            )}

            {loading ? (
              <DataTableSkeleton rows={8} columns={8} />
            ) : data.length === 0 ? (
              <AdminEmptyState
                icon="ri-coin-line"
                title="No commissions found"
                description={
                  status === "pending"
                    ? "No pending commissions. Set Status to “All statuses” to see approved or paid rows, or place a test order via an affiliate ?tk= link (incognito customer)."
                    : "Commissions appear here only when checkout sends a valid tracking code (?tk=). If orders were placed without that, the list stays empty."
                }
              />
            ) : (
              <DataTable
                columns={columns}
                data={data}
                customStyles={dataTableCustomStyles}
                pagination
                paginationServer
                paginationTotalRows={totalRecords}
                paginationDefaultPage={page}
                onChangePage={setPage}
                paginationPerPage={limit}
                paginationRowsPerPageOptions={[10, 20, 50]}
                onChangeRowsPerPage={() => {}}
                paginationComponentOptions={dataTablePaginationOptions}
                selectableRows
                selectableRowsHighlight
                selectableRowDisabled={selectableRowDisabled}
                onSelectedRowsChange={({ selectedRows: rows }) => setSelectedRows(rows)}
                highlightOnHover
                pointerOnHover={false}
              />
            )}
          </Card.Body>
        </Card>

        <Modal show={showDetail} onHide={() => setShowDetail(false)} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Commission Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <CommissionDetailContent commission={detailCommission} loading={detailLoading} />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowDetail(false)}>Close</Button>
          </Modal.Footer>
        </Modal>

        <Modal show={Boolean(rejectTarget)} onHide={() => setRejectTarget(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Reject Commission</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted small">
              Order {rejectTarget?.orderNumber || "—"} · {formatCurrency(rejectTarget?.commissionAmount, rejectTarget?.currency)}
            </p>
            <Form.Group>
              <Form.Label>Reason (required)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g. Order refunded / invalid attribution"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button
              variant="danger"
              disabled={!rejectReason.trim() || actionLoadingId === getCommissionId(rejectTarget)}
              onClick={handleReject}
            >
              Reject commission
            </Button>
          </Modal.Footer>
        </Modal>

        <Footer />
      </div>
    </React.Fragment>
  );
}
